import AdminChatbotLeads from "../pages/adminPages/AdminChatbotLeads";
import { Navigate, Route, Routes } from "react-router-dom";
import SpecialOfferPage from "../pages/specialOffer/SpecialOfferPage";
import InstagramUpdatesPage from "../pages/instagram/InstagramUpdatesPage";
import FacebookUpdatesPage from "../pages/facebook/FacebookUpdatesPage";
import Layout from "../components/layout/Layout";
import { isAdmin, isLoggedIn } from "../utils/getUserRole";
import {
  AdminAttInventory,
  AdminBuildLeads,
  AdminFinancePage,
  AdminFreight,
  AdminFreightCustomers,
  AdminFreightJobs,
  AdminFreightQuotes,
  AdminLayoutLazy,
  AdminOrders,
  AdminPackageForm,
  AdminPackagesList,
  AdminProfile,
  AdminSettings,
  AdminSpareParts,
  AdminUsers,
  Attachments,
  BecomeDealerPage,
  BlogPage,
  Categories,
  Checkout,
  CollectionDetailPage,
  CompatibilityRules,
  ConfigureForklift,
  ContactPage,
  ContractDetails,
  CustomerLogin,
  CustomerRegister,
  DealerApplicationPage,
  DealerFormPage,
  DiscountCodesPage,
  FinancePage,
  FreightDashboard,
  FreightDashboardLayout,
  FreightHome,
  FreightJobs,
  FreightLayout,
  FreightLogin,
  FreightPrivacy,
  FreightProfile,
  FreightQuotes,
  FreightRegister,
  FreightTerms,
  Home,
  Login,
  AboutPage,
  MyOrders,
  OrderDetail,
  PackageDetail,
  PackagesPage,
  PaymentOption,
  PrivacyPolicy,
  ProductConfigurator,
  ProductDetailPage,
  Products,
  QuoteManagement,
  StandardQuotes,
  StandardQuotePublicPage,
  Register,
  SparePartsDetailPage,
  SparePartsPage,
  TermsAndConditions,
  ThankYou,
  UserProfile,
  UsersProducts,
  VideosPage,
  VideoDetailPage,
} from "./routePreloader";

import AdminSpecialOfferLeads from "../pages/adminPages/AdminSpecialOfferLeads";
import AdminSpecialOfferManager from "../pages/adminPages/AdminSpecialOfferManager";
import StaffContractPage from "../pages/StaffContractPage";

const PublicRoute = ({ children }) => {
  if (isLoggedIn()) {
    return <Navigate to={isAdmin() ? "/admin" : "/"} replace />;
  }

  return children;
};

const UserOnlyRoute = ({ children }) => {
  if (!isLoggedIn()) return <Navigate to="/login" replace />;
  if (isAdmin()) return <Navigate to="/admin" replace />;

  return children;
};

const AdminOnlyRoute = ({ children }) => {
  if (!isLoggedIn()) return <Navigate to="/login" replace />;
  if (!isAdmin()) return <Navigate to="/" replace />;

  return children;
};

const UserPublicRoute = ({ children }) => {
  if (isLoggedIn() && isAdmin()) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

const isFreightLoggedIn = () => {
  const companyLoggedIn =
    localStorage.getItem("freightCompany") &&
    localStorage.getItem("freightToken");

  const customerLoggedIn =
    localStorage.getItem("customer") &&
    localStorage.getItem("customerToken");

  return Boolean(companyLoggedIn || customerLoggedIn);
};

const FreightPrivateRoute = ({ children }) => {
  if (!isFreightLoggedIn()) {
    return <Navigate to="/freight/login" replace />;
  }

  return children;
};

const FreightPublicRoute = ({ children }) => {
  if (isFreightLoggedIn()) {
    return <Navigate to="/freight/dashboard" replace />;
  }

  return children;
};

export default function AppRoutes() {
  return (
    <Routes>
      <Route
        element={
          <UserPublicRoute>
            <Layout />
          </UserPublicRoute>
        }
      >
        <Route path="/" element={<Home />} />
        <Route path="/special-offer" element={<SpecialOfferPage />} />
        <Route path="/instagram-updates" element={<InstagramUpdatesPage />} />
        <Route path="/facebook-updates" element={<FacebookUpdatesPage />} />

        <Route
          path="/facebook"
          element={<Navigate to="/facebook-updates" replace />}
        />

        <Route
          path="/instagram"
          element={<Navigate to="/instagram-updates" replace />}
        />

        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />

        <Route path="/machines" element={<UsersProducts />} />
        <Route
          path="/find-my-machine"
          element={<Navigate to="/machines" replace />}
        />
        <Route path="/collections/:handle" element={<CollectionDetailPage />} />
        <Route path="/spareparts" element={<SparePartsPage />} />
        <Route path="/spareparts/:handle" element={<SparePartsDetailPage />} />
        <Route path="/:slug" element={<ProductDetailPage />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route
          path="/termsandconditions"
          element={<Navigate to="/terms-and-conditions" replace />}
        />
        <Route
          path="/terms"
          element={<Navigate to="/terms-and-conditions" replace />}
        />
        <Route path="/package/:slug" element={<PackageDetail />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/blogs" element={<BlogPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/packages" element={<PackagesPage />} />
        <Route path="/profile" element={<UserProfile />} />
        <Route path="/my-orders" element={<MyOrders />} />
        <Route path="/contracts/:id" element={<ContractDetails />} />
        <Route path="/my-orders/:orderId" element={<OrderDetail />} />
        <Route path="/videos" element={<VideosPage />} />
        <Route path="/videos/:slug" element={<VideoDetailPage />} />
        <Route path="/finance" element={<FinancePage />} />
        <Route path="/become-a-dealer" element={<BecomeDealerPage />} />
        <Route path="/applying-dealer" element={<DealerFormPage />} />
      </Route>

      <Route path="/configure-machine/:id" element={<ConfigureForklift />} />
      <Route path="/quote/:token" element={<StandardQuotePublicPage />} />

      <Route
        element={
          <UserOnlyRoute>
            <Layout />
          </UserOnlyRoute>
        }
      >
        <Route path="/payment-options" element={<PaymentOption />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/thank-you" element={<ThankYou />} />
      </Route>

      <Route
        path="/admin"
        element={
          <AdminOnlyRoute>
            <AdminLayoutLazy />
          </AdminOnlyRoute>
        }
      >
        <Route index element={<Products />} />

        <Route
          path="/admin/special-offer-leads"
          element={
            <AdminOnlyRoute>
              <AdminSpecialOfferLeads />
            </AdminOnlyRoute>
          }
        />

        <Route
          path="/admin/special-offers"
          element={
            <AdminOnlyRoute>
              <AdminSpecialOfferManager />
            </AdminOnlyRoute>
          }
        />

        <Route
          path="/admin/manual-contracts"
          element={
            <AdminOnlyRoute>
              <StaffContractPage />
            </AdminOnlyRoute>
          }
        />

        <Route path="categories" element={<Categories />} />
        <Route path="inventory" element={<AdminAttInventory />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="finance" element={<AdminFinancePage />} />
        <Route path="leads" element={<AdminBuildLeads />} />
        <Route path="chatbot-leads" element={<AdminChatbotLeads />} />
        <Route path="products" element={<Products />} />
        <Route
          path="product-configurator/:id"
          element={<ProductConfigurator />}
        />
        <Route path="spare-parts" element={<AdminSpareParts />} />
        <Route path="attachments" element={<Attachments />} />
        <Route path="compatibility-rules" element={<CompatibilityRules />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="profile" element={<AdminProfile />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="packages" element={<AdminPackagesList />} />
        <Route path="packages/create" element={<AdminPackageForm />} />
        <Route path="packages/edit/:id" element={<AdminPackageForm />} />
        <Route
          path="dealers-applications"
          element={<DealerApplicationPage />}
        />
        <Route path="discounts" element={<DiscountCodesPage />} />

        <Route path="freight/companies" element={<AdminFreight />} />
        <Route path="freight/jobs" element={<AdminFreightJobs />} />
        <Route path="freight/quotes" element={<AdminFreightQuotes />} />
        <Route
          path="freight/customers"
          element={<AdminFreightCustomers />}
        />

        <Route path="request-quotes" element={<QuoteManagement />} />

        <Route path="standard-quotes" element={<StandardQuotes />} />
        <Route path="standard-quotes/new" element={<StandardQuotes />} />
        <Route
          path="standard-quotes/:id/edit"
          element={<StandardQuotes />}
        />
      </Route>

      <Route
        path="/freight"
        element={
          <FreightPrivateRoute>
            <FreightDashboardLayout />
          </FreightPrivateRoute>
        }
      >
        <Route path="dashboard" element={<FreightDashboard />} />
        <Route path="profile" element={<FreightProfile />} />
        <Route path="jobs" element={<FreightJobs />} />
        <Route path="quotes" element={<FreightQuotes />} />
      </Route>

      <Route
        path="/freight"
        element={
          <FreightPublicRoute>
            <FreightLayout />
          </FreightPublicRoute>
        }
      >
        <Route index element={<FreightHome />} />
        <Route path="login" element={<FreightLogin />} />
        <Route path="register" element={<FreightRegister />} />
        <Route path="customer-login" element={<CustomerLogin />} />
        <Route path="customer-register" element={<CustomerRegister />} />
        <Route path="terms" element={<FreightTerms />} />
        <Route path="privacy" element={<FreightPrivacy />} />
      </Route>
    </Routes>
  );
}